import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    GraduationCap,
    BookOpenText,
    CreditCard,
    UserCheck,
    Globe2,
    User as UserIcon,
    LogOut,
    Menu,
    X,
    TrendingUp,
    Mail,
} from "lucide-react";
import { FaGraduationCap } from "react-icons/fa";

const navSections = [
    {
        label: "Overview",
        items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
    },
    {
        label: "People",
        items: [
            { to: "/users", label: "Users", icon: Users },
            { to: "/teachers", label: "Teachers", icon: GraduationCap },
        ],
    },
    {
        label: "Content",
        items: [{ to: "/topics", label: "Topics & Questions", icon: BookOpenText }],
    },
    {
        label: "Marketing",
        items: [
            { to: "/webinar", label: "Webinar", icon: Mail },
            { to: "/parent-webinar", label: "Parents' Webinar", icon: Users },
        ],
    },
    {
        label: "Revenue",
        items: [
            { to: "/subscription", label: "Subscriptions", icon: CreditCard },
            { to: "/referall", label: "Referral Codes", icon: UserCheck },
        ],
    },
    {
        label: "Insights",
        items: [
            { to: "/visitors", label: "Visits", icon: Globe2 },
            { to: "/school-performance", label: "School Performance", icon: TrendingUp },
        ],
    },
];

const NavItem = ({ to, label, icon: Icon, active, onClick }) => (
    <Link
        to={to}
        onClick={onClick}
        className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${active
            ? "bg-white text-[#2148C0] shadow-sm"
            : "text-blue-100 hover:bg-white/10 hover:text-white"
            }`}
    >
        <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-200 ${active
                ? "bg-blue-50 text-[#2148C0]"
                : "bg-white/10 text-blue-50 group-hover:bg-white/20"
                }`}
        >
            <Icon size={18} strokeWidth={1.8} />
        </span>
        <span className="tracking-tight">{label}</span>
        {active && (
            <span className="absolute right-3 h-2 w-2 rounded-full bg-[#FFD700]" />
        )}
    </Link>
);

const Sidebar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const closeSidebar = () => setIsOpen(false);

    const handleLogout = () => {
        localStorage.clear();
        closeSidebar();
        navigate("/");
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-[#0B1D50]/60 backdrop-blur-sm z-40 lg:hidden"
                    onClick={closeSidebar}
                />
            )}

            <aside
                className={`fixed top-0 left-0 h-screen w-72 z-50 transform transition-transform duration-300 ease-out
                    bg-gradient-to-b from-[#2148C0] via-[#1B45BF] to-[#0F2669]
                    shadow-2xl shadow-[#0B1D50]/30
                    lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
                {/* Decorative glow blobs */}
                <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-blue-400 mix-blend-screen opacity-30 blur-3xl" />
                <div className="pointer-events-none absolute bottom-12 -right-20 h-48 w-48 rounded-full bg-[#FFD700] mix-blend-screen opacity-20 blur-3xl" />

                <div className="relative flex flex-col h-full">
                    {/* Brand */}
                    <div className="flex items-center justify-between px-5 pt-6 pb-5 border-b border-white/10">
                        <Link
                            to="/dashboard"
                            onClick={closeSidebar}
                            className="flex items-center gap-3"
                        >
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-lg">
                                <FaGraduationCap className="text-[#2148C0] text-xl" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold tracking-tight text-white leading-none">
                                    TeeChaa
                                </h2>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-blue-100 mt-1">
                                    Admin Console
                                </p>
                            </div>
                        </Link>
                        <button
                            className="text-blue-100 lg:hidden hover:bg-white/10 p-2 rounded-lg"
                            onClick={closeSidebar}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Nav */}
                    <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
                        {navSections.map((section) => (
                            <div key={section.label}>
                                <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.22em] uppercase text-blue-200/70">
                                    {section.label}
                                </p>
                                <div className="space-y-1">
                                    {section.items.map((item) => (
                                        <NavItem
                                            key={item.to}
                                            {...item}
                                            active={
                                                pathname === item.to ||
                                                (item.to !== "/dashboard" &&
                                                    pathname.startsWith(item.to))
                                            }
                                            onClick={closeSidebar}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </nav>

                    {/* Footer */}
                    <div className="px-3 pb-6 pt-4 border-t border-white/10 space-y-1">
                        <NavItem
                            to="/profile"
                            label="Profile"
                            icon={UserIcon}
                            active={pathname === "/profile"}
                            onClick={closeSidebar}
                        />
                        <button
                            onClick={handleLogout}
                            className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-100 hover:bg-red-500/20 hover:text-white transition-all duration-200"
                        >
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-red-100 group-hover:bg-red-500/30">
                                <LogOut size={18} strokeWidth={1.8} />
                            </span>
                            <span>Log out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile hamburger */}
            <button
                className={`fixed top-4 left-4 z-50 lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#2148C0] text-white shadow-lg shadow-[#2148C0]/30 hover:bg-blue-700 transition-all ${isOpen ? "hidden" : ""}`}
                onClick={() => setIsOpen(true)}
            >
                <Menu size={22} />
            </button>
        </>
    );
};

export default Sidebar;
